<template>
  <div class="rebook-search-button">
    <Button
      :class="{
        'button--primary': !isCalendar,
        'button--secondary': isCalendar,
      }"
      :disabled="disabled"
      @click="debouncedRebookSearch"
    >
      <template v-if="isLoading">
        <Spinner size="small" />
      </template>
      <template v-else>
        <slot name="icon"></slot>
        <span class="rebook-search-button__label">
          {{ buttonText || $t('afterSale.rebook.search.continue') }}
        </span>
        <slot name="icon-right"></slot>
      </template>
    </Button>
  </div>
</template>

<script lang="ts">
  import { defineComponent, PropType, ref } from 'vue'
  import { useRouter } from 'vue-router'
  import { useJourneyStore } from '@/stores/journeyStore'
  import { useBookingStore } from '@/stores/bookingStore'
  import { rebookSearch } from '@/services/journeyService'
  import { useDebounce } from '@/composables/useDebounce'
  import Button from '@/components/BaseComponents/Button.vue'
  import Spinner from '@/components/BaseComponents/Spinner/Spinner.vue'

  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate)
    return date.toISOString().split('T')[0]
  }

  export default defineComponent({
    name: 'RebookSearchButton',
    components: {
      Button,
      Spinner,
    },
    props: {
      bookingNumber: {
        type: String,
        required: true,
      },
      itemsToReplace: {
        type: Array as PropType<string[]>,
        required: true,
      },
      direction: {
        type: String as PropType<'outbound' | 'inbound' | 'entire'>,
        required: true,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      isCalendar: {
        type: Boolean,
        default: false,
      },
      buttonText: {
        type: String,
        default: '',
      },
    },
    setup(props, { emit }) {
      const router = useRouter()
      const journeyStore = useJourneyStore()
      const bookingStore = useBookingStore()
      const { debounce } = useDebounce()
      const isLoading = ref(false)

      const extractProductTypes = (segments: any[] | undefined): string[] => {
        if (!segments || segments.length === 0) return []

        const productTypes = new Set<string>()

        segments.forEach((segment) => {
          if (segment.required_products) {
            segment.required_products.forEach((product: any) => {
              if (product.type) {
                productTypes.add(product.type)
              }
            })
          }

          if (segment.additional_products) {
            segment.additional_products.forEach((product: any) => {
              if (product.type) {
                productTypes.add(product.type)
              }
            })
          }
        })

        return Array.from(productTypes)
      }

      const handleRebookSearch = async (event: Event) => {
        isLoading.value = true

        if (event) {
          event.preventDefault()
        }

        try {
          const allPassengers = bookingStore.bookingData?.passengers || []

          const activePassengers = allPassengers.filter(
            (passenger) => !passenger.cancel_timestamp
          )

          journeyStore.setPassengers(
            activePassengers.map((passenger) => ({
              id: passenger.id,
              type: passenger.type,
              first_name: passenger.first_name || '',
              last_name: passenger.last_name || '',
              email: passenger.email || '',
              phone_number: passenger.phone_number || '',
              discount_cards: passenger.discount_cards || [],
              protect_privacy: passenger.protect_privacy || false,
            }))
          )

          const passengers =
            activePassengers.map((passenger) => ({
              id: passenger.id,
              discount_cards:
                passenger.discount_cards?.map((card) => ({
                  code: card.code,
                })) || [],
            })) || []

          const outboundProductTypes = extractProductTypes(
            bookingStore.bookingData?.outbound_booking_tariff_segments
          )
          const inboundProductTypes = extractProductTypes(
            bookingStore.bookingData?.inbound_booking_tariff_segments
          )

          const defaultProductTypes = ['ST', 'TO']
          const userVisibleProductTypes =
            outboundProductTypes.concat(inboundProductTypes)

          const outboundCombinedProductTypes = [
            ...new Set([...defaultProductTypes, ...outboundProductTypes]),
          ]
          const inboundCombinedProductTypes = [
            ...new Set([...defaultProductTypes, ...inboundProductTypes]),
          ]

          const allSearchProductTypes = [
            ...new Set([
              ...outboundCombinedProductTypes,
              ...inboundCombinedProductTypes,
            ]),
          ]

          journeyStore.setRequestedProductTypes([
            ...new Set(userVisibleProductTypes),
          ])

          const travels: Array<{
            direction: 'outbound' | 'inbound'
            departure: string
            destination: string
            origin: string
            product_types: string[]
          }> =
            props.direction === 'entire'
              ? [
                  {
                    origin: journeyStore.OriginStation._u_i_c_station_code,
                    destination:
                      journeyStore.destinationStation._u_i_c_station_code,
                    direction: 'outbound',
                    departure: formatDate(journeyStore.departureDate),
                    product_types: outboundCombinedProductTypes,
                  },
                  {
                    origin: journeyStore.destinationStation._u_i_c_station_code,
                    destination: journeyStore.OriginStation._u_i_c_station_code,
                    direction: 'inbound' as 'inbound',
                    departure: formatDate(journeyStore.returnDate),
                    product_types: inboundCombinedProductTypes,
                  },
                ]
              : [
                  {
                    origin: journeyStore.OriginStation._u_i_c_station_code,
                    destination:
                      journeyStore.destinationStation._u_i_c_station_code,
                    direction: props.direction.toLowerCase() as
                      | 'outbound'
                      | 'inbound',
                    departure: formatDate(
                      props.direction === 'outbound'
                        ? journeyStore.departureDate
                        : journeyStore.returnDate
                    ),
                    product_types:
                      props.direction.toLowerCase() === 'outbound'
                        ? outboundCombinedProductTypes
                        : inboundCombinedProductTypes,
                  },
                ]

          const response = await rebookSearch(props.bookingNumber, {
            intention: 'rebook',
            passengers,
            replace_item_ids: props.itemsToReplace,
            travels,
          })

          journeyStore.setJourneySearchResults(response)

          if (response && response.offer) {
            journeyStore.checkMissingProductTypes()
          } else {
            console.warn(
              'Response has no offer, skipping missing product types check'
            )
          }

          if (!props.isCalendar) {
            router.push({
              name: 'RebookingSearchResultPage',
              query: {
                bookingId: props.bookingNumber,
                rebookingType: props.direction,
                itemsToReplace: props.itemsToReplace.join(','),
                isRebooking: 'true',
              },
            })
          }
        } catch (error) {
          console.error('RebookSearchButton - rebookSearch failed:', error)
        } finally {
          isLoading.value = false
        }
      }

      const debouncedRebookSearch = debounce(handleRebookSearch, 300)

      return {
        debouncedRebookSearch,
        isLoading,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .rebook-search-button {
    :deep(.button) {
      display: inline-flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;

      .rebook-calendar-search__buttons & {
        background-color: transparent;
        border: none;
        width: auto;

        &:disabled {
          background: transparent;
          border: none;
          color: $clr-text-disable;

          &:hover {
            background: transparent;
          }
        }
      }
    }
  }
</style>
